import { useEffect, useRef, useCallback } from "react";
import { Platform } from "react-native";

interface UseFocusReturnOptions {
  /** Whether to capture and track focus (typically when modal is open) */
  isActive: boolean;
  /** Optional specific element to return focus to */
  triggerElement?: HTMLElement | null;
}

interface UseFocusReturnReturn {
  /** Ref to optionally attach to the trigger element */
  triggerRef: React.RefObject<HTMLElement>;
  /** Manually trigger focus return */
  returnFocus: () => void;
}

/**
 * Hook to manage focus return after a modal/dialog closes.
 * Captures the active element when activated and returns focus when deactivated.
 *
 * @example
 * // Basic usage - auto-captures active element
 * const { returnFocus } = useFocusReturn({ isActive: isModalOpen });
 *
 * // With explicit trigger ref
 * const { triggerRef, returnFocus } = useFocusReturn({ isActive: isModalOpen });
 * <button ref={triggerRef} onClick={() => setIsModalOpen(true)}>
 *   Open Modal
 * </button>
 */
export function useFocusReturn({
  isActive,
  triggerElement,
}: UseFocusReturnOptions): UseFocusReturnReturn {
  const triggerRef = useRef<HTMLElement>(null);
  const capturedElementRef = useRef<HTMLElement | null>(null);

  // Capture the active element when becoming active
  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") {
      return;
    }

    if (isActive) {
      // Priority: explicit triggerElement > triggerRef > document.activeElement
      capturedElementRef.current =
        triggerElement ??
        triggerRef.current ??
        (document.activeElement as HTMLElement);
    }
  }, [isActive, triggerElement]);

  // Return focus when deactivating
  useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }

    return () => {
      if (!isActive && capturedElementRef.current) {
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          if (
            capturedElementRef.current &&
            typeof capturedElementRef.current.focus === "function"
          ) {
            capturedElementRef.current.focus();
          }
        });
      }
    };
  }, [isActive]);

  // Manual focus return function
  const returnFocus = useCallback(() => {
    if (Platform.OS !== "web") {
      return;
    }

    if (
      capturedElementRef.current &&
      typeof capturedElementRef.current.focus === "function"
    ) {
      capturedElementRef.current.focus();
    }
  }, []);

  return {
    triggerRef,
    returnFocus,
  };
}

export default useFocusReturn;
